import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import semver from 'semver';
import posthog from 'posthog-js'; 

// --- DODANE IMPORTY ---
import { Purchases } from '@revenuecat/purchases-capacitor'; // <--- IMPORT REVENUECAT
// ----------------------

import api from './api/apiHelper';
import { SafeArea } from '@capacitor-community/safe-area';
import { useStatusBarDim } from './hooks/useStatusBarDim';

// --- Components & Services ---
import { LoadingProvider } from './contexts/LoadingContext';
import InitialLoader from './components/common/InitialLoader';
import GlobalSpinner from './components/common/GlobalSpinner';
import './services/pwaInstaller';

// --- PUSH NOTIFICATIONS ---
import { syncPushTokenIfGranted, setupPushMessageListeners } from './services/pushNotifications'; 
// --------------------------

// ... (Twoje importy stron) ...
import UpdateRequiredScreen from './components/pages/UpdateRequiredScreen';
import Login from "./components/pages/Login";
import Register from "./components/pages/Register";
import Settings from "./components/pages/Settings";
import Upgrade from './components/pages/Upgrade';
import PaymentStatus from './components/pages/PaymentStatus';
import Dashboard from "./components/pages/Dashboard";
import DashboardDes from "./components/pages/DashboardDes";
import Training from "./components/pages/Training";
import SolveProblem from "./components/pages/SolveProblem";
import TutorialProblem from "./components/pages/TutorialProblem";
import SolveProblemDes from "./components/pages/SolveProblemDes";
import EndSessionStats from "./components/pages/EndSessionStats";
import SessionStats from "./components/pages/SessionStats";
import ViewSolvedProblem from "./components/pages/ViewSolvedProblem";
import UserStats from "./components/pages/UserStats";
import PasswordReset from "./components/pages/PasswordReset";
import PasswordResetConfirm from "./components/pages/PasswordResetConfirm";
import Start1 from "./components/pages/Start1";
import Start2 from "./components/pages/Start2";
import ThankYouPage from "./components/pages/ThankYouPage";
import DlaSzkol from "./components/pages/DlaSzkol";
import ProblemRenderTestScreen from "./components/pages/ProblemRenderTestScreen";
import ProblemRenderMaturaScreen from "./components/pages/ProblemRenderMaturaScreen";
import MaturaProblemsAdminPage from "./components/pages/MaturaProblemsAdminPage";
import AIStagingAdminPage from "./components/pages/AIStagingAdminPage";
import PPOpenAuditAdminPage from "./components/pages/PPOpenAuditAdminPage";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsOfService from "./components/pages/TermsOfService";
import AppleCallback from './components/auth/AppleCallback';
import CompleteProfile from './components/pages/CompleteProfile';
import LandingPage from './components/pages/TikTokLanding'; 
import LandingPage2 from './components/pages/TikTokLandingV2'; 
import RequestDeleteAccount from './components/pages/RequestDeleteAccount';
import ExamSheetStarter from "./components/pages/ExamSheetStarter";
import ExamSheetResolver from "./components/pages/ExamSheetResolver";
import MaturaSheetManager from "./components/pages/MaturaSheetManager";
import MagicLogin from './components/pages/MagicLogin';
import SupportPage from './components/pages/SupportPage';
import TutorialExamResolver from './components/pages/TutorialExamResolver';
import ProtectedRoute from "./components/ProtectedRoute";
import AuthRedirect from "./components/AuthRedirect";
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import { UserDashboardProvider } from './contexts/UserDashboardContext';
import CookieConsentBannerComponent from './contexts/CookieConsentBannerComponent';
import ReconsentModal from './components/ReconsentModal';
import SurefiresScreen from "./components/pages/SurefiresScreen";
import SolveSurefire from "./components/pages/SolveSurefire";
import NiepewniakiScreen from "./components/pages/NiepewniakiScreen";
import SolveNiepewniak from "./components/pages/SolveNiepewniak";
import { setGamificationConfig } from './utils/gamificationConfig';
import { invalidateApiCache, writeApiCache, fieldDetailsCacheKey } from './utils/apiCache';

const MainRouteHandler = () => {
  const isMobileApp = Capacitor.isNativePlatform();
  if (isMobileApp) {
    return <Navigate to="/login" replace />;
  }
  return <AuthRedirect><Start2 /></AuthRedirect>;
};

const AppRoutes = ({ setProblemsData, setUserEloDict, problem, examAttemptData, setExamAttemptData }) => {
  const location = useLocation();
  const routes = (
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<MainRouteHandler />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/start1" element={<Start1 />} />
        <Route path="/start2" element={<Start2 />} />
        <Route path="/info" element={<Start2 />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/dla-szkol" element={<DlaSzkol />} />
        <Route path="/tiktok" element={<LandingPage />} />
        <Route path="/start" element={<LandingPage />} />
        <Route path="/start-v2" element={<LandingPage2 />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/request-delete-account" element={<RequestDeleteAccount />} />
        <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
        <Route path="/auto-login" element={<MagicLogin />} />
        <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} />
        <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
        <Route path="/password_reset" element={<PasswordReset />} />
        <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />
        <Route path="/login/apple/callback" element={<AppleCallback />} />
        <Route path="/tutorial-exam" element={<TutorialExamResolver />} />
        <Route path="/list_problem_test" element={<ProblemRenderTestScreen />} />
        <Route path="/list_problem_matura" element={<ProblemRenderMaturaScreen />} />
        <Route path="/admin/matura-problems" element={<MaturaProblemsAdminPage />} />
        <Route path="/admin/ai-staging" element={<AIStagingAdminPage />} />
        <Route path="/admin/pp-open-audit" element={<PPOpenAuditAdminPage />} />
        <Route path="/admin/matura-sheets" element={<MaturaSheetManager />} />
        <Route path="/app" element={<ProtectedRoute><Dashboard setProblemsData={setProblemsData} setUserEloDict={setUserEloDict} /></ProtectedRoute>} />
        <Route path="/training" element={<ProtectedRoute><Training setProblemsData={setProblemsData} setUserEloDict={setUserEloDict} /></ProtectedRoute>} />
        <Route path="/surefires" element={<ProtectedRoute><SurefiresScreen /></ProtectedRoute>} />
        <Route path="/solve-surefire" element={<ProtectedRoute><SolveSurefire /></ProtectedRoute>} />
        <Route path="/niepewniaki" element={<ProtectedRoute><NiepewniakiScreen /></ProtectedRoute>} />
        <Route path="/solve-niepewniak" element={<ProtectedRoute><SolveNiepewniak /></ProtectedRoute>} />
        <Route path="/app-des" element={<ProtectedRoute><DashboardDes setProblemsData={setProblemsData} setUserEloDict={setUserEloDict} /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
        <Route path="/payment-status" element={<ProtectedRoute><PaymentStatus /></ProtectedRoute>} />
        <Route path="/tutorial" element={<ProtectedRoute><TutorialProblem /></ProtectedRoute>} />
        <Route path="/solve-problem" element={<ProtectedRoute><SolveProblem /></ProtectedRoute>} />
        <Route path="/solve-problem-des" element={<ProtectedRoute><SolveProblemDes /></ProtectedRoute>} />
        <Route path="/end-session-stats" element={<ProtectedRoute><EndSessionStats /></ProtectedRoute>} />
        <Route path="/session-stats" element={<ProtectedRoute><SessionStats /></ProtectedRoute>} />
        <Route path="/view-solved-problem" element={<ProtectedRoute><ViewSolvedProblem problem={problem} /></ProtectedRoute>} />
        <Route path="/user-stats" element={<ProtectedRoute><UserStats /></ProtectedRoute>} />
        <Route path="/start-exam" element={<ProtectedRoute><ExamSheetStarter setExamAttemptData={setExamAttemptData} /></ProtectedRoute>} />
        <Route path="/solve-exam-sheet" element={<ProtectedRoute><ExamSheetResolver examAttemptData={examAttemptData} setExamAttemptData={setExamAttemptData} /></ProtectedRoute>} />
      </Routes>
  );

  // mode="wait" wszędzie — sekwencyjny fade przejść między ekranami (oryginalny, ulubiony feel).
  return (
    <AnimatePresence mode="wait">
      {routes}
    </AnimatePresence>
  );
};

const App = () => {
  const { isConsentLoaded, showBanner, syncAnalyticsWithAccount } = useCookieConsent();
  
  // Global states
  const [problemsData, setProblemsData] = useState([]);
  const [userEloDict, setUserEloDict] = useState({});
  const [problemsSession, setProblemsSession] = useState([]); 
  const [problem, setProblem] = useState(null); 
  const [sessionStartTime, setSessionStartTime] = useState(null); 
  const [examAttemptData, setExamAttemptData] = useState(null);

  const [reconsentNeeded, setReconsentNeeded] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isUpdateRequired, setIsUpdateRequired] = useState(false);

  useEffect(() => {
    invalidateApiCache('surefire-atoms');
    invalidateApiCache('exams');
    invalidateApiCache('user_stats');
    invalidateApiCache('fields_preferences');
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SafeArea.enable({ config: { customColorsForSystemBars: false } }).catch(() => {});
    }
  }, []);

  // Globalnie wyszarza gorny pasek systemowy gdy otwarty jest dowolny modal z ciemnym scrimem.
  useStatusBarDim();

useEffect(() => {
    let customerInfoListener = null;

    const setupPaymentListener = async () => {
      if (Capacitor.isNativePlatform()) {
        console.log("App.js: Inicjalizacja globalnego listenera płatności...");
        
        try {
            customerInfoListener = await Purchases.addCustomerInfoUpdateListener(async (info) => {
              
              // --- FIX: ZABEZPIECZENIE PRZED NULL ---
              if (!info || !info.entitlements) {
                  // Czasami mostek natywny zwraca null przy inicjalizacji. Ignorujemy to.
                  return;
              }
              // -------------------------------------

              const entitlement = info.entitlements.active?.['premium']; // Używamy też optional chaining (?.) dla bezpieczeństwa
              
              if (entitlement) {
                console.log("GLOBAL LISTENER: Wykryto Premium! Wymuszam synchronizację danych...");
                invalidateApiCache('user_dashboard_data');
                invalidateApiCache('surefire-atoms');
                invalidateApiCache('exams');
              }
            });
        } catch (err) {
            console.error("App.js: Błąd konfiguracji listenera RC:", err);
        }
      }
    };

    setupPaymentListener();

    return () => {
      // Cleanup jeśli potrzebny
    };
  }, []);
  // -----------------------------------------------------------------------

  // Status bar pozostaje widoczny i przezroczysty (edge-to-edge). NIE chowamy go:
  // chowanie powodowało, że WebView nie pokrywał czubka ekranu i backdrop modali
  // zostawiał jasny pasek u góry. Edge-to-edge obsługuje @capacitor-community/safe-area.

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 1. Global config
        // Start appki zakrywa InitialLoader — globalny spinner (min. 500 ms) mrugał po nim.
        const { data: appConfig } = await api.get('/app-config/', { skipLoading: true });
        
        if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    const minVersions = appConfig.min_versions || {};
    const requiredVersion = minVersions[platform];
    
    const appInfo = await CapacitorApp.getInfo();
    const currentVersion = semver.coerce(appInfo.version)?.version; // "31.0" → "31.0.0"

    if (requiredVersion && currentVersion && semver.lt(currentVersion, requiredVersion)) {
        setIsUpdateRequired(true);
        setIsAuthLoading(false);
        return;
    }
}
        localStorage.setItem('global_config', JSON.stringify(appConfig));
        setGamificationConfig(appConfig);
        
        // 2. User Check
        const token = localStorage.getItem('token');
        const prefsRaw = localStorage.getItem('user_preferences');

        if (token) {
          syncPushTokenIfGranted();      
          setupPushMessageListeners();   

          try {
            const promises = [];

            // A. Preferences
            if (!prefsRaw) {
               console.log("App: Fetching preferences...");
               promises.push(
                   api.get('/api/users/me/preferences/', { skipLoading: true }).then(res => {
                       localStorage.setItem('user_preferences', JSON.stringify(res.data));
                   })
               );
            }

            // B. User Data & Consents
            const userDataPromise = api.get('/users/me/', { skipLoading: true }).then(res => {
                console.log("App: User data fetched.");

                // --- !!! CRITICAL POSTHOG IDENTIFY (ZMARTWYCHWSTANIE) !!! ---
                if (res.data && res.data.id) {
                    console.log(`[PostHog] Identifying user: ${res.data.id}`);
                    posthog.identify(res.data.id);
                    
                    posthog.people.set({
                        platform: Capacitor.getPlatform(),
                        is_mobile_app: Capacitor.isNativePlatform(),
                        user_id:res.data.id
                    });
                }
                
                if (res.data.account_status) {
                    syncAnalyticsWithAccount(res.data.account_status.analytics_allowed);
                }
                const consents = res.data.consent_status;
                const needed = Object.entries(consents).find(
                  ([key, value]) => value.requires_reconsent === true
                );
                if (needed) {
                  setReconsentNeeded({
                    type: needed[0].toUpperCase(),
                    data: needed[1]
                  });
                }
            });
            promises.push(userDataPromise);

            await Promise.all(promises);

            api.get('surefire-atoms/', { skipLoading: true }).then(r => writeApiCache('surefire-atoms', r.data)).catch(() => {});
            api.get('exams/', { skipLoading: true }).then(r => writeApiCache('exams', r.data)).catch(() => {});
            api.get('user_stats_view/', { skipLoading: true }).then(r => writeApiCache('user_stats', r.data)).catch(() => {});
            api.get('fields_preferences/', { skipLoading: true }).then(r => writeApiCache('fields_preferences', r.data)).catch(() => {});
            api.get('field-details-bulk/', { skipLoading: true }).then(r => {
              Object.entries(r.data).forEach(([fieldId, subfields]) => {
                writeApiCache(fieldDetailsCacheKey(fieldId), subfields);
              });
            }).catch(() => {});

          } catch (error) {
            console.error("App: User init error:", error);
          }
        } else {
          console.log("App: No user token. PostHog remains anonymous.");
        }

      } catch (error) {
         console.error("App: Critical init error:", error);
      } finally {
         setIsAuthLoading(false);
      }
    };

    initializeApp();
  }, [syncAnalyticsWithAccount]);

  
  if (isAuthLoading) {
    return <InitialLoader />;
  }

  if (isUpdateRequired) {
    return <UpdateRequiredScreen />;
  }

  return (
    <>
      <GlobalSpinner />
      <Router>
        <AppRoutes
          setProblemsData={setProblemsData}
          setUserEloDict={setUserEloDict}
          problem={problem}
          examAttemptData={examAttemptData}
          setExamAttemptData={setExamAttemptData}
        />
      </Router>
      
      {!Capacitor.isNativePlatform() && isConsentLoaded && showBanner && <CookieConsentBannerComponent />}

      {reconsentNeeded && (
        <ReconsentModal 
          consentInfo={reconsentNeeded} 
          onAccepted={() => setReconsentNeeded(null)}
        />
      )}
    </>
  );
};

const AppWithProviders = () => (
  <CookieConsentProvider>
    <LoadingProvider>
      <UserDashboardProvider>
        <App />
      </UserDashboardProvider>
    </LoadingProvider>
  </CookieConsentProvider>
);

export default AppWithProviders;